import React, { useState, useEffect } from 'react';
import Sidebar from './Sidebar';
import HeaderBar from './HeaderBar';
import '../../App.scss';
import ProfileTab from '../items/ProfileTab';
import ObjectsTab from '../items/ObjectsTab';
import RequestsTab from '../items/RequestsTab';
import MessagesTab from '../items/MessagesTab';
import DashboardTab from '../items/DashboardTab';
import DataTab from '../items/DataTab';
import ObjectDetails from '../items/ObjectDetails';
import SettingsTab from '../items/SettingsTab';
import NDATab from '../items/tabs/NDATab';
import CRMTab from '../items/CRMTab';
// Watchlist now uses ObjectsTab with `watchlist` prop
import { TabRouterProps, AppSettings } from './interfaces/ITabRouter';
import ContactForm from '../dialogs/SupportContactForm';
import { useMsal } from '@azure/msal-react';
import { useHUser } from '../items/hooks/HUsers';
import { User } from '../items/interfaces/IUsers';
import { UserProvider } from './contexts/UserContext';

/**
 * TabRouter
 * Main router component for switching between tabs and rendering content.
 * @component
 * @param {TabRouterProps} props - Props for TabRouter.
 * @returns {JSX.Element} Tab router component.
 */
// Tab enum for type safety and maintainability
export enum Tab {
    DASHBOARD = 'dashboard',
    OBJECTS = 'objects',
    OBJECT_DETAILS = 'objectdetails',
    WATCHLIST = 'watchlist',
    MA = 'acquisitions',
    COLLECTIONS = 'collections',
    FUNDS = 'funds',
    ANALYTICS = 'analytics',
    NDAS = 'ndas',
    PROFILE = 'profile',
    CRM = 'crm',
    SETTINGS = 'settings',
    API = 'api',
    REQUESTS = 'requests',
    MESSAGES = 'messages',
}
export enum UserRole {
    ADMIN = 'Admin',
    BUSINESS = 'Business',
    MM = 'MM',
    OPS = 'OPS',
    ALL = 'ALL',
    TRADING = 'TRADING',
    DeFi = 'DeFi',
    Validator = 'Validator',
}
const TabGroups: Record<Tab, UserRole[]> = {
    [Tab.DASHBOARD]: [UserRole.ALL],
    [Tab.OBJECTS]: [UserRole.ALL],
    [Tab.OBJECT_DETAILS]: [UserRole.ALL],
    [Tab.MA]: [UserRole.ALL],
    [Tab.COLLECTIONS]: [UserRole.ALL],
    [Tab.FUNDS]: [UserRole.ALL],
    [Tab.ANALYTICS]: [UserRole.ALL],
    [Tab.NDAS]: [UserRole.ALL],
    [Tab.CRM]: [UserRole.ALL],
    [Tab.PROFILE]: [UserRole.ALL],
    [Tab.API]: [UserRole.ALL],
    [Tab.SETTINGS]: [UserRole.ALL],
    [Tab.REQUESTS]: [UserRole.ALL],
    [Tab.MESSAGES]: [UserRole.ALL],
    [Tab.WATCHLIST]: [UserRole.ALL],
};

function isTabVisible(tab: Tab, dbUser: User | null): boolean {
    const groups = TabGroups[tab];
    if (groups.includes(UserRole.ALL) || dbUser?.role === UserRole.ADMIN) return true;
    if (!dbUser || !dbUser.role) return false;
    // If the user's role matches any group for the tab, allow access
    return groups.includes(dbUser.role as UserRole);
}

// Helper to DRY up tab rendering with authorization
function renderTab({
    tab,
    dbUser,
    children,
    extraCondition = true,
    notAuthorizedMessage = 'You are not authorized to see this page.',
}: {
    tab: Tab;
    dbUser: User | null;
    children: React.ReactNode;
    extraCondition?: boolean;
    notAuthorizedMessage?: string;
}) {
    if (!extraCondition) return null;
    if (isTabVisible(tab, dbUser)) {
        return <>{children}</>;
    }
    return <div style={{ padding: 32, color: 'red', fontWeight: 600 }}>{notAuthorizedMessage}</div>;
}

const TabRouter = ({ activeTab, setActiveTab, loading, ...props }: TabRouterProps) => {
    const [theme, setTheme] = useState<'light' | 'dark'>(
        () => (localStorage.getItem('theme') as 'light' | 'dark') || 'light'
    );

    const { accounts } = useMsal();
    const msalUser = accounts[0];

    // --- User DB integration ---
    const { users, fetchUsers } = useHUser();
    const [dbUser, setDbUser] = useState<User | null>(null);

    useEffect(() => {
        fetchUsers();
    }, [fetchUsers]); // Now safe to include fetchUsers since it's memoized with useCallback

    useEffect(() => {
        if (msalUser && users && users.length > 0) {
            

            // Try several matching strategies to be robust across different user shapes
            const normalize = (s: any) => (s && String(s).toLowerCase().trim()) || '';
            const msalName = normalize(
                msalUser?.username ?? (msalUser as any)?.preferred_username ?? (msalUser as any)?.name ?? (msalUser as any)?.upn ?? ''
            );

            const found = users.find((u: User) => {
                const candidates = [u.user, (u as any).email, (u as any).username, (u as any).upn];
                return candidates.some((c) => normalize(c) && (normalize(c) === msalName || msalName.includes(normalize(c)) || normalize(c).includes(msalName)));
            });

            

            try {
                console.debug('[TabRouter] msalUser:', msalUser);
                console.debug('[TabRouter] users count:', users.length);
                console.debug('[TabRouter] matched db user:', found);
                console.debug('[TabRouter] matched db user NDA:', found?.nda);
            } catch (e) {}
            setDbUser(found || null);
        }
    }, [msalUser, users]);

    useEffect(() => {
        document.body.classList.remove('theme-light', 'theme-dark');
        document.body.classList.add(`theme-${theme}`);
        localStorage.setItem('theme', theme);
    }, [theme]);

    // Sync tab with URL hash (for browser navigation)
    useEffect(() => {
        const handleHashChange = () => {
            try {
                console.debug('[TabRouter] handleHashChange fired; hash=', window.location.hash);
            } catch (e) {}
            const hash = window.location.hash.replace('#', '');

            // Check for client positions with client parameter
            const clientPositionsMatch = hash.match(/^clientpositions\/(.+)$/);
            if (clientPositionsMatch) {
                const clientValue = decodeURIComponent(clientPositionsMatch[1]);
                handleTabClick('clientpositions', clientValue);
                // Clean up URL to prevent re-triggering
                window.history.replaceState(null, '', '#clientpositions');
                return;
            }
            const accountAnalyticsMatch = hash.match(/^accountanalytics\/(.+)$/);
            if (accountAnalyticsMatch) {
                const accountValue = decodeURIComponent(accountAnalyticsMatch[1]);
                handleTabClick('accountanalytics', accountValue);
                window.history.replaceState(null, '', '#accountanalytics');
                return;
            }
            const objectDetailsMatch = hash.match(/^objectdetails\/(.+)$/);
            if (objectDetailsMatch) {
                const objectId = decodeURIComponent(objectDetailsMatch[1]);
                setCurrentObject(objectId);
                setCurrentObjectData(null);
                setCurrentTab((prev) => {
                    if (prev === Tab.OBJECT_DETAILS) return prev;
                    setActiveTab && setActiveTab(Tab.OBJECT_DETAILS);
                    return Tab.OBJECT_DETAILS;
                });
                return;
            }
            // Standard tab handling
            if (hash && Object.values(Tab).includes(hash as Tab)) {
                setCurrentTab((prev) => {
                    if (prev === hash) return prev;
                    setActiveTab && setActiveTab(hash as Tab);
                    return hash;
                });
            }
        };
        window.addEventListener('hashchange', handleHashChange);
        // On mount, check hash
        handleHashChange();
        return () => window.removeEventListener('hashchange', handleHashChange);
    });

    

    const [currentTab, setCurrentTab] = useState<string>(activeTab);
    const [showSupportForm, setShowSupportForm] = useState(false);
    const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
    const [currentObject, setCurrentObject] = useState<string | null>(null);
    const [currentObjectData, setCurrentObjectData] = useState<any | null>(null);
    // Used to force HeaderBar to re-mount and refresh
    const [headerKey, setHeaderKey] = useState(0);

    useEffect(() => {
        try {
            console.debug('[TabRouter] currentTab changed ->', currentTab);
        } catch (e) {}
    }, [currentTab]);

    // Sample user and app settings (you can replace with your actual data)
    const appSettings: AppSettings = {
        appName: 'XM',
        //logo: "/assets/favicon.ico" // Path to your logo
    };


    const handleSidebarNavigation = (route: string, label: string) => {
        // Map sidebar routes to your existing tab system using enum values
        // Normalize route to lowercase for case-insensitive lookup
        const normalizedRoute = route.toLowerCase();
            const routeMapping: { [key: string]: Tab } = {
            objects: Tab.OBJECTS,
            watchlist: Tab.WATCHLIST,
            dashboard: Tab.DASHBOARD,
            acquisitions: Tab.MA,
            ndas: Tab.NDAS,
                crm: Tab.CRM,
            collections: Tab.COLLECTIONS,
            funds: Tab.FUNDS,
            analytics: Tab.ANALYTICS,
            profile: Tab.PROFILE,
            settings: Tab.SETTINGS,
            requests: Tab.REQUESTS,
            messages: Tab.MESSAGES,
        };
        // Debug: log navigation attempts
        try {

        } catch (e) {}
        if (normalizedRoute === 'logout') {
            // Handle logout logic here
            return;
        }
        if (normalizedRoute === 'support') {
            // Open the global contact/support form
            setShowSupportForm(true);
            return;
        }
        const mappedTab = routeMapping[normalizedRoute];
            if (mappedTab) {
            try {
                console.debug('[TabRouter] mappedTab ->', mappedTab);
            } catch (e) {}
            setCurrentTab(mappedTab);
            setActiveTab(mappedTab);
            setHeaderKey((k) => k + 1);
            // Update the URL hash for browser navigation only if different
            try {
                const newHash = `#${mappedTab}`;
                if (window.location.hash !== newHash) {
                    window.location.hash = newHash;
                }
            } catch (e) {}
        }
        // Only close sidebar for mobile, keep open for desktop
        // (Sidebar always visible on desktop)
    };

    // Called from AccountsTab
    const handleTabClick = (tab: string, account?: any) => {
        // Normalize tab to Tab enum if passed as string
        const tabEnum = Object.values(Tab).includes(tab as Tab)
            ? (tab as Tab)
            : Tab[tab.toUpperCase() as keyof typeof Tab] || tab;
        setCurrentTab(tabEnum);
        if (tabEnum === Tab.OBJECT_DETAILS) {
            const objectId = account?.id ?? account;
            setCurrentObject(objectId !== undefined && objectId !== null ? String(objectId) : null);
            setCurrentObjectData(account && typeof account === 'object' ? account : null);
        }
        // For tabs that require an account (positions), set currentAccount
     

        if (setActiveTab) setActiveTab(tabEnum);
    };

    // dbUser is the User from DB matching the current MSAL user
    useEffect(() => {
        if (currentTab === Tab.REQUESTS) {
            try {

            } catch (e) {}
        }
    }, [currentTab, dbUser, props.user]);

    return (
        <UserProvider dbUser={dbUser}>
            <div className={`app-layout ${theme}`}>
                {/* Sidebar on the left */}
                <Sidebar
                    activeRoute={currentTab}
                    onNavigate={(route, label) => {
                        handleSidebarNavigation(route, label);
                        setMobileSidebarOpen(false);
                    }}
                    onClose={() => setMobileSidebarOpen(false)}
                    user={(props.user as any) || dbUser}
                    appSettings={appSettings}
                    theme={theme}
                    isOpen={mobileSidebarOpen}
                    onProfileClick={() => {
                        setActiveTab(Tab.PROFILE);
                        setCurrentTab(Tab.PROFILE);
                        setHeaderKey((k) => k + 1);
                        window.location.hash = `#${Tab.PROFILE}`;
                    }}
                />

                {/* Main content area with header and body */}
                <div className='main-area'>
                    <HeaderBar
                        key={headerKey}
                        theme={theme}
                        setTheme={setTheme}
                        user={(props.user as any) || dbUser}
                        onMenuToggle={() => setMobileSidebarOpen((prev) => !prev)}
                        onProfileClick={() => {
                            setActiveTab(Tab.PROFILE);
                            setCurrentTab(Tab.PROFILE);
                            setHeaderKey((k) => k + 1);
                            window.location.hash = `#${Tab.PROFILE}`;
                        }}
                    />
                    <div className='main-body'>

                            {showSupportForm && (
                                <div className="support-form-overlay">
                                    {(() => {
                                        const profileUser = dbUser || (props.user as any);
                                        const email = profileUser?.email ?? '';
                                        return (
                                            <>
                                                <ContactForm initialEmail={email} onSent={() => setShowSupportForm(false)} onClose={() => setShowSupportForm(false)} />
                                                <button className="support-close" onClick={() => setShowSupportForm(false)}>Close</button>
                                            </>
                                        );
                                    })()}
                                </div>
                            )}


                        {(() => {
                            const profileUser = dbUser || (props.user as any);

                            return renderTab({
                                tab: Tab.PROFILE,
                                dbUser,
                                extraCondition: currentTab === Tab.PROFILE,
                                children: (
                                    <ProfileTab
                                        {...({
                                            loading,
                                            onTabClick: handleTabClick,
                                            isLoggedIn: props.isLoggedIn,
                                            user: profileUser,
                                            ...props,
                                        } as any)}
                                    />
                                ),
                            });
                        })()}


                        {renderTab({
                            tab: Tab.FUNDS,
                            dbUser,
                            extraCondition: currentTab === Tab.FUNDS,
                               children: <ObjectsTab onTabClick={handleTabClick} user={dbUser} filter='funds' {...props} />,
                        })}
                       {renderTab({
                            tab: Tab.DASHBOARD,
                            dbUser,
                            extraCondition: currentTab === Tab.DASHBOARD,
                               children: <DashboardTab onTabClick={handleTabClick} user={dbUser} filter='RE' {...props} />,
                        })}
                        {renderTab({
                            tab: Tab.MA,
                            dbUser,
                            extraCondition: currentTab === Tab.MA,
                            children: <ObjectsTab onTabClick={handleTabClick} user={dbUser} filter='MA' {...props} />,
                        })}


                        {renderTab({
                            tab: Tab.COLLECTIONS,
                            dbUser,
                            extraCondition: currentTab === Tab.COLLECTIONS,
                               children: <ObjectsTab onTabClick={handleTabClick} user={dbUser} filter='collections' {...props} />,
                        })}

                        {renderTab({
                            tab: Tab.OBJECTS,       
                            dbUser,
                            extraCondition: currentTab === Tab.OBJECTS,
                            children: <ObjectsTab onTabClick={handleTabClick} user={dbUser} {...props} />,
                        })}
                        {renderTab({
                            tab: Tab.WATCHLIST,
                            dbUser,
                            extraCondition: currentTab === Tab.WATCHLIST,
                            children: <ObjectsTab onTabClick={handleTabClick} user={dbUser} watchlist={true} {...props} />,
                        })}
                        {renderTab({
                            tab: Tab.OBJECT_DETAILS,
                            dbUser,
                            extraCondition: currentTab === Tab.OBJECT_DETAILS,
                            children: (
                                <ObjectDetails
                                    objectId={currentObject}
                                    initialObject={currentObjectData}
                                    user={(currentObjectData && (currentObjectData as any).user) ?? dbUser ?? (props.user as any)}
                                    onBack={() => {
                                        setCurrentTab(Tab.OBJECTS);
                                        setActiveTab && setActiveTab(Tab.OBJECTS);
                                        window.location.hash = `#${Tab.OBJECTS}`;
                                    }}
                                />
                            ),
                        })}
                        {renderTab({
                            tab: Tab.ANALYTICS,
                            dbUser,
                            extraCondition: currentTab === Tab.ANALYTICS,
                            children: <DataTab onTabClick={handleTabClick} {...props} />,
                        })}
                        {renderTab({
                            tab: Tab.NDAS,
                            dbUser,
                            extraCondition: currentTab === Tab.NDAS,
                            children: <NDATab />,
                        })}
                        {renderTab({
                            tab: Tab.CRM,
                            dbUser,
                            extraCondition: currentTab === Tab.CRM,
                            children: <CRMTab />,
                        })}
                        {renderTab({
                            tab: Tab.SETTINGS,
                            dbUser,
                            extraCondition: currentTab === Tab.SETTINGS,
                            children: <SettingsTab />,
                        })}
                        {renderTab({
                            tab: Tab.MESSAGES,
                            dbUser,
                            extraCondition: currentTab === Tab.MESSAGES,
                            children: <MessagesTab user={(props.user as any) || dbUser} />,
                        })}
                        {renderTab({
                            tab: Tab.REQUESTS,
                            dbUser,
                            extraCondition: currentTab === Tab.REQUESTS,
                            children: <RequestsTab user={(props.user as any) || dbUser}  />, 
                        })}

                    </div>
                </div>
            </div>
        </UserProvider>
    );
};
export default TabRouter;
// --- end TabRouter ---
